import { yupResolver } from '@hookform/resolvers/yup';
import {
  DocumentTemplateType,
  SmtpSubtenantSettingsPatchDto,
  QueryFactory,
  DocumentMailSettingsPatchDto,
  VisitMailSettingsPatchDto,
  MergeFieldQueryType,
} from '@lmt/shared-types';

import { Field } from '@lmt/unicorn-shared/app/components/common/Field';
import { FieldBlock } from '@lmt/unicorn-shared/app/components/common/FieldBlock';
import { Loading } from '@lmt/unicorn-shared/app/components/common/Loading';
import {
  Button,
  ButtonColor,
} from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/Button';
import { SubmitButton } from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/SubmitButton';
import { CheckBox } from '@lmt/unicorn-shared/app/components/common/ui-kit/CheckBox';
import { TimePicker } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/date-time/TimePicker';
import { HookFormDropDownInput } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/dropdown/DropDownInput';
import { Input } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/Input';
import { pages } from '@lmt/unicorn-shared/app/constants/sharedRoutes';
import { useTabs } from '@lmt/unicorn-shared/app/features/navigation/operations';
import { useAdvancedForm } from '@lmt/unicorn-shared/app/helpers/form/advanced-form';
import { handleSubmitFormError } from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { useCustomCompareMemo } from '@lmt/unicorn-shared/app/helpers/useCustomCompareMemo';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';
import equal from 'fast-deep-equal';
import i18next from 'i18next';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Controller } from 'react-hook-form';
import { boolean, number, string } from 'yup';
import * as yup from 'yup';
import { useGetTemplateQuery } from '../../../documents/richedit/OpenTemplatesRichEditMenuItem';
import generalStyles from '../SettingsBlock.module.scss';
import styles from './MailSettings.module.scss';
import GrayCross from '@lmt/unicorn-shared/src/app/icons/gray-cross.svg?react';
import { useEventCallback } from '@mui/material';
import { useGlobalForms } from '../../../global-forms/useGlobalForms';
import { MonacoExpressionBuilder } from '@lmt/unicorn-shared/app/common/Monaco/MonacoExpressionBuilder';
import { expressionFunctions } from '@lmt/expressions/src/expressions/helpers/expression-functions';
import { getFunctionDeclarations } from '../../../documents/components/intellisense-helper';
import { nameof } from '@lmt/unicorn-shared/app/helpers/nameof';
import { SubtenantSettingsKeys } from '../types';
import { useRegisterUnsavedChanges } from '../useRegisterUnsavedChanges';

type Settings = {
  visitMailSettings?: VisitMailSettingsPatchDto;
  smtpSettings: SmtpSubtenantSettingsPatchDto;
  documentMailSettings?: DocumentMailSettingsPatchDto;
};

function t(value: string, options?: any) {
  return i18next.t(`translation:errors.${value}` as any, options);
}

type Props = { subtenantId: number };

const defaultSettingsForSubtenant: Settings = {
  visitMailSettings: {
    enabled: false,
    visitNotificationSubject: 'Coming visit notification',
    visitNotificationTemplateId: 0,
    visitNotificationDaysBeforeNotify: 0,
    visitNotificationRunJobAtInMillsAfterMidnight: 0,
  },
  smtpSettings: {
    host: 'smtp.example.com',
    port: 25,
    username: '',
    fromName: 'Unicorn',
    fromAddress: 'info@unicorn.com',
  },
  documentMailSettings: {
    enabled: false,
    notificationTemplateId: 0,
    notificationSubject: 'Patient information',
    isEncryptDocument: false,
    passwordExpression: null,
  },
};

const settingsYupSchema: yup.SchemaOf<Settings> = yup.object({
  visitMailSettings: yup.object({
    enabled: boolean().required(),
    visitNotificationDaysBeforeNotify: number()
      .transform((value) => (isNaN(value) ? undefined : value))
      .when('enabled', {
        is: true,
        then: (schema) =>
          schema
            .required(t('required'))
            .min(0, t('min_number', { minNumber: 0 }))
            .max(365, t('max_number', { maxNumber: 365 })),
      }),
    visitNotificationRunJobAtInMillsAfterMidnight: number()
      .transform((value) => (isNaN(value) ? undefined : value))
      .when('enabled', {
        is: true,
        then: (schema) => schema.required(t('required')).min(0),
      }),
    visitNotificationSubject: string()
      .trim()
      .when('enabled', {
        is: true,
        then: (schema) => schema.required(t('required')),
      }),
    visitNotificationTemplateId: number()
      .transform((value) => (isNaN(value) || value === 0 ? undefined : value))
      .when('enabled', {
        is: true,
        then: (schema) => schema.required(t('required')),
      }),
  }),
  smtpSettings: yup
    .object({
      host: string()
        .trim()
        .when(['$isVisitMailEnabled', '$isDocumentMailEnabled'], {
          is: (isVisitMailEnabled: any, isDocumentMailEnabled: any) =>
            isVisitMailEnabled || isDocumentMailEnabled,
          then: (schema) => schema.required(t('required')),
        }),
      port: number()
        .nullable()
        .transform((value) => (isNaN(value) ? null : value))
        .when(['$isVisitMailEnabled', '$isDocumentMailEnabled'], {
          is: (isVisitMailEnabled: any, isDocumentMailEnabled: any) =>
            isVisitMailEnabled || isDocumentMailEnabled,
          then: (schema) =>
            schema
              .required(t('required'))
              .min(0, t('min_number', { minNumber: 0 }))
              .max(65535, t('max_number', { maxNumber: 65535 })),
        }),
      username: string()
        .nullable()
        .trim()
        .transform((value) =>
          value != null && value.length != 0 ? value : null,
        )
        .notRequired(),
      password: string().notRequired(),
      fromAddress: string()
        .trim()
        .email(() => t('email'))
        .when(['$isVisitMailEnabled', '$isDocumentMailEnabled'], {
          is: (isVisitMailEnabled: any, isDocumentMailEnabled: any) =>
            isVisitMailEnabled || isDocumentMailEnabled,
          then: (schema) => schema.required(t('required')),
        }),
      fromName: string()
        .trim()
        .when(['$isVisitMailEnabled', '$isDocumentMailEnabled'], {
          is: (isVisitMailEnabled: any, isDocumentMailEnabled: any) =>
            isVisitMailEnabled || isDocumentMailEnabled,
          then: (schema) => schema.required(t('required')),
        }),
    })
    .defined(),
  documentMailSettings: yup.object({
    enabled: boolean().required(),
    notificationSubject: string()
      .nullable()
      .trim()
      .when('enabled', {
        is: true,
        then: (schema) => schema.required(t('required')),
      }),
    notificationTemplateId: number()
      .transform((value) => (isNaN(value) || value === 0 ? undefined : value))
      .when('enabled', {
        is: true,
        then: (schema) => schema.required(t('required')),
      }),
    isEncryptDocument: boolean().when('enabled', {
      is: true,
      then: (schema) => schema.required(t('required')),
    }),
    passwordExpression: string()
      .nullable()
      .trim()
      .when(['enabled', 'isEncryptDocument'], {
        is: (enabled: any, isEncryptDocument: any) =>
          isEncryptDocument && enabled,
        then: (schema) => schema.required(t('required')),
      }),
  }),
});

export const MailSettings: FC<Props> = function MailSettings(props: Props) {
  const selectedSubtenantId = props.subtenantId;
  const modals = useModal();
  const tabs = useTabs();
  const globalForms = useGlobalForms();
  const i18nSettings = useScopedTranslation('subtenant_settings.mail_settings');
  const {
    data: visitMailSettings,
    isLoading: isLoadingMailSettings,
    refetch: refetchMailSettings,
  } = QueryFactory.VisitMailServiceSettingsQuery.useGetQuery(
    selectedSubtenantId!,
    {
      enabled: !!selectedSubtenantId,
    },
  );

  const {
    data: smtpSettings,
    isLoading: isLoadingSmtpSettings,
    refetch: refetchSmtpSettings,
  } = QueryFactory.SmtpSettingsQuery.useGetSubtenantSettingsQuery(
    selectedSubtenantId!,
    {
      enabled: !!selectedSubtenantId,
    },
  );

  const {
    data: documentMailSettings,
    isLoading: isLoadingDocumentMailSettings,
    refetch: refetchDocumentMailSettings,
  } = QueryFactory.DocumentMailSettingsQuery.useGetDocumentMailSettingsQuery(
    selectedSubtenantId!,
    {
      enabled: !!selectedSubtenantId,
    },
  );

  const settings = smtpSettings && {
    visitMailSettings: visitMailSettings && { ...visitMailSettings },
    smtpSettings: { ...smtpSettings },
    documentMailSettings: documentMailSettings && { ...documentMailSettings },
  };

  const { data: templates } = useGetTemplateQuery(
    DocumentTemplateType.Document,
  );

  const [newPassword, setNewPassword] = useState<string | null>(null);
  const [isVisitMailEnabled, setIsVisitMailEnabled] = useState(
    visitMailSettings?.enabled ?? false,
  );
  const [isDocumentMailEnabled, setIsDocumentMailEnabled] = useState(
    documentMailSettings?.enabled ?? false,
  );

  const mergeFieldQuery = QueryFactory.MergeFieldQuery.useGetQueriesQuery();
  const intellisenseContent = useMemo(() => {
    if (!mergeFieldQuery.data) return '';
    const documentQueries = mergeFieldQuery.data ?? [];

    return [
      ...expressionFunctions.map((x) => `declare function ${x.signature};`),
      getFunctionDeclarations(
        ['patient'],
        'PatientType',
        documentQueries,
        MergeFieldQueryType.PatientQuery,
      ),
      getFunctionDeclarations(
        ['visit'],
        'VisitType',
        documentQueries,
        MergeFieldQueryType.VisitQuery,
      ),
      getFunctionDeclarations(
        ['Case'],
        'CaseType',
        documentQueries,
        MergeFieldQueryType.CaseQuery,
      ),
    ].join('\n');
  }, [mergeFieldQuery.data]);

  const form = useAdvancedForm<Settings>(
    useCallback(
      async (data) => {
        try {
          data.smtpSettings.password =
            newPassword != null ? newPassword : undefined;

          const mailDto = data.visitMailSettings;
          const smtpDto = data.smtpSettings;
          const documentMailDto = data.documentMailSettings;
          await QueryFactory.VisitMailServiceSettingsClient.patch(
            selectedSubtenantId!,
            mailDto!,
          );
          await QueryFactory.SmtpSettingsClient.patchSubtenantSettings(
            selectedSubtenantId!,
            smtpDto,
          );
          await QueryFactory.DocumentMailSettingsClient.patchDocumentMailSettings(
            selectedSubtenantId!,
            documentMailDto!,
          );

          await refetchMailSettings();
          await refetchSmtpSettings();
          await refetchDocumentMailSettings();

          setNewPassword(null);
        } catch (e) {
          const errorDetails = handleSubmitFormError(e, setError);
          if (errorDetails.overallServerError) {
            await modals.showError({ text: errorDetails.overallServerError });
          }
        }
      },
      [selectedSubtenantId, newPassword],
    ),
    useCustomCompareMemo(() => settings, [settings], equal),
    {
      shouldResetOnSuccess: true,
      mode: 'onSubmit',
      shouldUnregister: false,
      defaultValues: defaultSettingsForSubtenant,
      resolver: yupResolver(settingsYupSchema),
      context: { isVisitMailEnabled, isDocumentMailEnabled },
    },
  );

  const { setError, formState, reset, register, watch } = form;
  const { isDirty, dirtyFields } = formState;

  const notificationsEnabled = watch('visitMailSettings.enabled');
  const selectedTemplateId = watch(
    'visitMailSettings.visitNotificationTemplateId',
  );
  const documentNotificationsEnabled = watch('documentMailSettings.enabled');
  const documentEncryptEnabled = watch(
    'documentMailSettings.isEncryptDocument',
  );
  const selectedDocumentNotificationTemplateId = watch(
    'documentMailSettings.notificationTemplateId',
  );

  useEffect(() => {
    const value = notificationsEnabled ?? false;
    setIsVisitMailEnabled(value);
  }, [notificationsEnabled]);

  useEffect(() => {
    const value = documentNotificationsEnabled ?? false;
    setIsDocumentMailEnabled(value);
  }, [documentNotificationsEnabled]);

  const onEditTemplateClick = (templateId: number | null | undefined) =>
    templateId &&
    tabs.openTab(
      pages.documentDesigner.link({
        templateId: templateId,
        templateType: DocumentTemplateType.Document,
      }),
    );

  const [documentPasswordKey, setDocumentPasswordKey] = useState(1);
  const onReset = useCallback(() => {
    if (settings) {
      reset(settings);
      setNewPassword(null);
      setDocumentPasswordKey((key) => ++key);
    }
  }, [visitMailSettings, smtpSettings, documentMailSettings]);

  const onSendTestMessage = useEventCallback(async () => {
    globalForms.openSendTestMessage(selectedSubtenantId);
  });

  useEffect(() => {
    onReset();
  }, [visitMailSettings, smtpSettings, documentMailSettings]);

  const isUnsavedChanges = useRegisterUnsavedChanges(
    SubtenantSettingsKeys.MailService,
    form,
    !!newPassword,
  );

  return (
    <Loading
      loading={
        isLoadingMailSettings ||
        isLoadingSmtpSettings ||
        isLoadingDocumentMailSettings
      }
    >
      <form onSubmit={form.handleSubmitDefault}>
        <div
          className={generalStyles.container}
          data-test-id="mail-service-settings-container"
        >
          <div className={generalStyles.header}>
            <div className={styles.row}>
              <div className={generalStyles.title}>
                {i18nSettings.t('mail_settings_title')}
              </div>
            </div>
            <div className={generalStyles.optionContainer}>
              <Button
                title={i18nSettings.t('send_test_message')}
                disabled={
                  (isDirty && !!dirtyFields.smtpSettings) ||
                  !!newPassword ||
                  !smtpSettings ||
                  Object.values(smtpSettings).some(
                    (x) =>
                      x === null ||
                      x === undefined ||
                      x.toString().trim().length == 0,
                  )
                }
                onClick={onSendTestMessage}
              />
              <Button
                disabled={!isUnsavedChanges}
                title={i18nSettings.t('reset_button_title')}
                className={generalStyles.option}
                color={ButtonColor.Secondary}
                onClick={onReset}
              />
              <SubmitButton
                disabled={!isUnsavedChanges}
                submitting={formState.isSubmitting}
                className={generalStyles.option}
                title={i18nSettings.t('submit_button_title')}
              />
            </div>
          </div>
          <div className={styles.fieldBlocksContainer}>
            <FieldBlock title={i18nSettings.t('smtp_settings_title')}>
              <div className={styles.row}>
                <Field title={i18nSettings.t('host')} className={styles.field}>
                  <Input
                    variant="normal"
                    {...register('smtpSettings.host')}
                    errorText={formState.errors.smtpSettings?.host?.message}
                  />
                </Field>
                <Field title={i18nSettings.t('port')} className={styles.field}>
                  <Input
                    variant="normal"
                    type="number"
                    size={4}
                    {...register('smtpSettings.port', {
                      valueAsNumber: true,
                      setValueAs: (v) => parseInt(v) ?? '',
                    })}
                    errorText={formState.errors.smtpSettings?.port?.message}
                  />
                </Field>
              </div>
              <div className={styles.row}>
                <Field
                  title={i18nSettings.t('username')}
                  className={styles.field}
                >
                  <Input
                    variant="normal"
                    {...register('smtpSettings.username')}
                    errorText={formState.errors.smtpSettings?.username?.message}
                  />
                </Field>
                <Field
                  title={i18nSettings.t('password')}
                  className={styles.field}
                >
                  {newPassword == null ? (
                    <Button
                      title={i18nSettings.t('change_password')}
                      onClick={() => setNewPassword('')}
                    />
                  ) : (
                    <Input
                      variant="normal"
                      type={'password'}
                      onChange={(e) => setNewPassword(e.currentTarget.value)}
                      endAdornment={
                        <GrayCross onClick={() => setNewPassword(null)} />
                      }
                    />
                  )}
                </Field>
              </div>
              <div className={styles.row}>
                <Field
                  title={i18nSettings.t('fromName')}
                  className={styles.field}
                >
                  <Input
                    variant="normal"
                    {...register('smtpSettings.fromName')}
                    errorText={formState.errors.smtpSettings?.fromName?.message}
                  />
                </Field>
                <Field
                  title={i18nSettings.t('fromAddress')}
                  className={styles.field}
                >
                  <Input
                    variant="normal"
                    {...register('smtpSettings.fromAddress')}
                    errorText={
                      formState.errors.smtpSettings?.fromAddress?.message
                    }
                  />
                </Field>
              </div>
            </FieldBlock>
            <div>
              <FieldBlock title={i18nSettings.t('email_settings_title')}>
                <div className={styles.row}>
                  <CheckBox
                    title={i18nSettings.t('enabled')}
                    {...register('visitMailSettings.enabled')}
                    className={styles.checkbox}
                  />
                </div>
                <div className={styles.row}>
                  <div>
                    <Field
                      title={i18nSettings.t('visitNotificationTemplateId')}
                      className={styles.field}
                      linkProps={{
                        title: 'Edit document',
                        onClick: () => onEditTemplateClick(selectedTemplateId),
                      }}
                    >
                      <HookFormDropDownInput
                        required={true}
                        control={form.control}
                        variant={'normal'}
                        name={'visitMailSettings.visitNotificationTemplateId'}
                        useIdFunctionAsValue={true}
                        options={templates ?? []}
                        idFunction={'id'}
                        getOptionLabel={'title'}
                        errorText={
                          notificationsEnabled
                            ? formState.errors.visitMailSettings
                                ?.visitNotificationTemplateId?.message
                            : ''
                        }
                        disabled={!notificationsEnabled}
                      />
                    </Field>
                    <Field
                      title={i18nSettings.t(
                        'visitNotificationDaysBeforeNotify',
                      )}
                      className={styles.field}
                    >
                      <Input
                        variant="normal"
                        type="number"
                        step={1}
                        {...register(
                          'visitMailSettings.visitNotificationDaysBeforeNotify',
                          {
                            valueAsNumber: true,
                          },
                        )}
                        errorText={
                          notificationsEnabled
                            ? formState.errors.visitMailSettings
                                ?.visitNotificationDaysBeforeNotify?.message
                            : ''
                        }
                        disabled={!notificationsEnabled}
                      />
                    </Field>
                  </div>
                  <div>
                    <Field
                      title={i18nSettings.t('visitNotificationSubject')}
                      className={styles.field}
                    >
                      <Input
                        variant="normal"
                        {...register(
                          'visitMailSettings.visitNotificationSubject',
                        )}
                        errorText={
                          notificationsEnabled
                            ? formState.errors.visitMailSettings
                                ?.visitNotificationSubject?.message
                            : ''
                        }
                        disabled={!notificationsEnabled}
                      />
                    </Field>
                    <Field
                      title={i18nSettings.t('visitNotificationRunJobAt')}
                      className={styles.field}
                    >
                      <Controller
                        control={form.control}
                        name={
                          'visitMailSettings.visitNotificationRunJobAtInMillsAfterMidnight'
                        }
                        render={({
                          field: { value, onChange },
                          fieldState: { error },
                        }) => (
                          <TimePicker
                            testId={'runJobAt'}
                            variant={'normal'}
                            timeInMills={value}
                            error={!!notificationsEnabled && !!error?.message}
                            errorText={error?.message}
                            onTimeChanged={(newTime) => onChange(newTime)}
                            disabled={!notificationsEnabled}
                          />
                        )}
                      />
                    </Field>
                  </div>
                </div>
              </FieldBlock>
              <FieldBlock
                title={i18nSettings.t('document_mail_settings_title')}
              >
                <div className={styles.row}>
                  <CheckBox
                    title={i18nSettings.t('enabled')}
                    {...register('documentMailSettings.enabled')}
                    className={styles.checkbox}
                  />
                </div>
                <div className={styles.row}>
                  <Field
                    title={i18nSettings.t('document_notification_templateId')}
                    className={styles.fieldHigher}
                    linkProps={{
                      title: 'Edit document',
                      onClick: () =>
                        onEditTemplateClick(
                          selectedDocumentNotificationTemplateId,
                        ),
                    }}
                  >
                    <HookFormDropDownInput
                      required={true}
                      control={form.control}
                      variant={'normal'}
                      name={'documentMailSettings.notificationTemplateId'}
                      useIdFunctionAsValue={true}
                      options={templates ?? []}
                      idFunction={'id'}
                      getOptionLabel={'title'}
                      errorText={
                        documentNotificationsEnabled
                          ? formState.errors.documentMailSettings
                              ?.notificationTemplateId?.message
                          : ''
                      }
                      disabled={!documentNotificationsEnabled}
                    />
                  </Field>
                  <Field
                    title={i18nSettings.t('document_notification_subject')}
                    className={styles.field}
                  >
                    <Input
                      variant="normal"
                      {...register('documentMailSettings.notificationSubject')}
                      errorText={
                        documentNotificationsEnabled
                          ? formState.errors.documentMailSettings
                              ?.notificationSubject?.message
                          : ''
                      }
                      disabled={!documentNotificationsEnabled}
                    />
                  </Field>
                </div>
                <div className={styles.row}>
                  <CheckBox
                    title={i18nSettings.t('document_password_enabled')}
                    {...register('documentMailSettings.isEncryptDocument')}
                    className={styles.field}
                    disabled={!documentNotificationsEnabled}
                  />
                </div>
                <div className={styles.row}>
                  <Field
                    title={i18nSettings.t('document_password')}
                    className={styles.fullField}
                  >
                    <MonacoExpressionBuilder
                      key={documentPasswordKey}
                      disabled={
                        !documentEncryptEnabled || !documentNotificationsEnabled
                      }
                      defaultExpressionValue={
                        documentMailSettings?.passwordExpression ?? undefined
                      }
                      onExpressionChanged={(e) => {
                        form.setValue(
                          nameof('documentMailSettings.passwordExpression'),
                          e,
                          {
                            shouldDirty: true,
                            shouldValidate: true,
                          },
                        );
                      }}
                      error={
                        !!documentEncryptEnabled &&
                        !!documentNotificationsEnabled &&
                        !!formState.errors.documentMailSettings
                          ?.passwordExpression?.message
                      }
                      errorText={
                        !!documentEncryptEnabled &&
                        !!documentNotificationsEnabled
                          ? formState.errors.documentMailSettings
                              ?.passwordExpression?.message
                          : ''
                      }
                      intellisenseContent={intellisenseContent}
                    />
                  </Field>
                </div>
              </FieldBlock>
            </div>
          </div>
        </div>
      </form>
    </Loading>
  );
};
